import React, { useState, useEffect } from 'react';
import { ApiClient } from '../../api/client';
import { DEPLOYMENT } from '../../config/deployment';
import type { SyncStatus, Tenant } from '../../types';
import {
  RefreshCw, Cloud, Database, HardDrive, ArrowDownRight, ArrowUpRight,
  CheckCircle2, AlertCircle, X, Building2, Globe, Sliders
} from 'lucide-react';

interface SyncManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SyncManagerModal: React.FC<SyncManagerModalProps> = ({ isOpen, onClose }) => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [remoteTenants, setRemoteTenants] = useState<Tenant[]>([]);
  const [selectedTenantKey, setSelectedTenantKey] = useState<string>('auto');
  // S14: nincs beégetett (valós) szervezetnév – az opcionális kiemelt szervezet a konfigurációból jön.
  const [customSlug, setCustomSlug] = useState<string>(DEPLOYMENT.featuredSyncSlug);
  const featuredSlug = DEPLOYMENT.featuredSyncSlug;
  const featuredName = DEPLOYMENT.featuredSyncName || featuredSlug;
  const [isLoading, setIsLoading] = useState(false);
  const [syncResult, setSyncResult] = useState<{ message: string; type: 'ok' | 'err'; stats?: any } | null>(null);

  // Távoli szerver kapcsolat & auth konfiguráció
  const [remoteUrl, setRemoteUrl] = useState<string>(() => {
    return localStorage.getItem('kf_remote_sync_url') || DEPLOYMENT.remoteApiUrl;
  });
  // S12: a távoli superadmin fiók adatai NEM lehetnek a forráskódban (a publikus
  // bundle-ből kiolvashatók lennének) – a felhasználó adja meg őket belépéskor.
  const [remoteEmail, setRemoteEmail] = useState<string>('');
  const [remotePassword, setRemotePassword] = useState<string>('');
  const [remoteToken, setRemoteToken] = useState<string>(() => {
    return localStorage.getItem('kf_remote_sync_token') || '';
  });
  const [remoteStatus, setRemoteStatus] = useState<'connected' | 'disconnected' | 'checking' | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  // Delta szinkron kapcsoló
  const [isDeltaSync, setIsDeltaSync] = useState(true);

  // Progress kijelzés
  const [progress, setProgress] = useState<{ step: string; percent: number; details: string[] } | null>(null);

  const fetchRemoteTenants = async (urlToCheck: string = remoteUrl, tokenToUse?: string) => {
    try {
      const res = await ApiClient.remoteRequest(urlToCheck, '/tenants', tokenToUse || remoteToken);
      if (res.ok && res.data) {
        const raw = res.data;
        const list = (Array.isArray(raw) ? raw : (raw.data || raw.tenants || [])) as Tenant[];
        setRemoteTenants(list);
      }
    } catch {
      // Nem kritikus
    }
  };

  const loadStatusAndTenants = async () => {
    setIsLoading(true);
    const [statusRes, tenantsRes] = await Promise.all([
      ApiClient.getSyncStatus(),
      ApiClient.getTenants(),
    ]);

    if (statusRes.ok && statusRes.data) {
      setSyncStatus(statusRes.data);
    }
    if (tenantsRes.ok && tenantsRes.data) {
      const list = tenantsRes.data as Tenant[];
      setTenants(list);
      // Ha van konfigurált kiemelt szervezet, előre kiválaszthatjuk
      const featured = featuredSlug ? list.find(t => t.slug === featuredSlug) : undefined;
      if (featured && selectedTenantKey === 'auto') {
        setSelectedTenantKey(`local_${featured.id}`);
      } else if (list.length > 0 && selectedTenantKey === 'auto') {
        setSelectedTenantKey(`local_${list[0].id}`);
      }
    }
    setIsLoading(false);
  };

  const checkRemoteConnection = async (urlToCheck: string = remoteUrl) => {
    setRemoteStatus('checking');
    try {
      const res = await ApiClient.remoteRequest(urlToCheck, '/auth/demo-users');
      if (res.ok) {
        setRemoteStatus('connected');
        fetchRemoteTenants(urlToCheck);
      } else {
        setRemoteStatus('disconnected');
      }
    } catch {
      setRemoteStatus('disconnected');
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadStatusAndTenants();
      setSyncResult(null);
      setProgress(null);
      checkRemoteConnection(remoteUrl);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleUrlChange = (newUrl: string) => {
    setRemoteUrl(newUrl);
    localStorage.setItem('kf_remote_sync_url', newUrl);
    setRemoteStatus(null);
    // A korábbi távoli munkamenet-token egy MÁSIK szerverhez tartozik: címváltáskor
    // eldobjuk, különben a régi (pl. éles) token a új címre (pl. VPS) is elküldésre kerülne.
    setRemoteToken('');
    localStorage.removeItem('kf_remote_sync_token');
  };

  // ── PUSH: Localhost / VPS → Felhő ────────────────────────────────
  const handlePush = async () => {
    setIsLoading(true);
    setSyncResult(null);
    setProgress({ step: 'Kapcsolódás a távoli felhőhöz...', percent: 10, details: [] });

    try {
      // 1. Távoli felhő autentikáció
      let token = remoteToken;
      if (!token) {
        setProgress(prev => ({
          step: 'Távoli felhő hitelesítés...',
          percent: 20,
          details: [...(prev?.details || []), `Bejelentkezés: ${remoteEmail} @ ${remoteUrl}`],
        }));

        if (!remoteUrl || !remoteEmail || !remotePassword) {
          // S12: nincs beégetett fiók – kérjük a kapcsolati adatokat.
          setShowSettings(true);
          throw new Error('Add meg a távoli szerver címét, valamint a superadmin e-mail-címét és jelszavát a Kapcsolat beállításai panelen.');
        }
        const authRes = await ApiClient.loginRemote(remoteUrl, remoteEmail, remotePassword);
        if (!authRes.ok || !authRes.token) {
          throw new Error(`Távoli felhő bejelentkezés sikertelen: ${authRes.error}`);
        }
        token = authRes.token;
        setRemoteToken(token);
        localStorage.setItem('kf_remote_sync_token', token);
      }

      // 2. Kijelölt szervezet azonosítása (Push-hoz helyi szervezet szükséges)
      let tenantIdToExport: number;
      let tenantName = '';

      if (selectedTenantKey.startsWith('local_')) {
        tenantIdToExport = Number(selectedTenantKey.replace('local_', ''));
        const t = tenants.find(x => x.id === tenantIdToExport);
        tenantName = t ? t.name : `Szervezet #${tenantIdToExport}`;
      } else {
        const slug = selectedTenantKey.startsWith('remote_') ? selectedTenantKey.replace('remote_', '') : customSlug.trim();
        const existingLocal = tenants.find(t => t.slug === slug);
        if (!existingLocal) {
          throw new Error(`A(z) "${slug}" szervezet még nem létezik a helyi példányon. Kérlek használd a "Változások Lehívása (Pull ← Felhő)" gombot az adatok letöltéséhez!`);
        }
        tenantIdToExport = existingLocal.id;
        tenantName = existingLocal.name;
      }

      setProgress({
        step: `Helyi adatok csomagolása (${tenantName})...`,
        percent: 35,
        details: [`Helyi adatok kiexportálása: ${tenantName} (ID: ${tenantIdToExport})`],
      });

      const lastSyncVer = isDeltaSync ? (syncStatus?.latestEventId || 0) : 0;
      const exportRes = await ApiClient.exportSyncPackage(tenantIdToExport, lastSyncVer);
      if (!exportRes.ok || !exportRes.package) {
        throw new Error(`Hiba az adatok kiexportálása során: ${exportRes.error || 'Ismeretlen hiba'}`);
      }

      const pkg = exportRes.package;
      setProgress(prev => ({
        step: 'Csomag beküldése a felhőbe (Adatbázis sémák & rekordok)...',
        percent: 55,
        details: [
          ...(prev?.details || []),
          `Csomag összeállítva: ${pkg.pages?.length || 0} oldal, ${pkg.records?.length || 0} rekord, ${pkg.attachments?.length || 0} melléklet-hivatkozás`,
          'Feltöltés és összefésülés a távoli Cloudflare D1 adatbázisban...',
        ],
      }));

      // 3. Csomag beküldése a távoli felhőbe
      const applyRes = await ApiClient.remoteRequest(remoteUrl, '/sync/apply-package', token, {
        method: 'POST',
        body: JSON.stringify({ package: pkg }),
      });

      if (!applyRes.ok) {
        throw new Error(`A távoli felhő elutasította a csomagot: ${applyRes.error}`);
      }

      setProgress(prev => ({
        step: 'Adatbázis sikeresen frissítve a felhőben!',
        percent: 75,
        details: [
          ...(prev?.details || []),
          `✓ Szervezet sikeresen létrejött/frissült: ${applyRes.tenant_slug || tenantName}`,
          `✓ Alkalmazott oldalak: ${applyRes.applied?.pages || 0}, rekordok: ${applyRes.applied?.records || 0}`,
        ],
      }));

      // 4. Hiányzó bináris fájlok streamelt átvitele az R2 tárolóba
      const missingFiles = applyRes.missing_files || [];
      if (missingFiles.length > 0) {
        setProgress(prev => ({
          step: `Hiányzó dokumentumok átvitele az R2 felhőtárhelyre (0/${missingFiles.length})...`,
          percent: 78,
          details: [
            ...(prev?.details || []),
            `📦 ${missingFiles.length} db dokumentum bináris feltöltése a felhőbe...`,
          ],
        }));

        let transferredCount = 0;
        const currentOrigin = window.location.origin;
        const localToken = ApiClient.getToken();

        for (let i = 0; i < missingFiles.length; i++) {
          const fileInfo = missingFiles[i];
          const transferRes = await ApiClient.transferSyncFile(
            currentOrigin,
            localToken,
            remoteUrl,
            token,
            fileInfo.r2_key,
            fileInfo.mime_type
          );

          if (transferRes.ok) {
            transferredCount++;
          }

          const pct = 78 + Math.round(((i + 1) / missingFiles.length) * 20);
          setProgress(prev => ({
            step: `Dokumentumok feltöltése (${transferredCount}/${missingFiles.length})...`,
            percent: pct,
            details: [
              ...(prev?.details || []),
              `✓ ${fileInfo.file_name} sikeresen feltöltve (${i + 1}/${missingFiles.length})`,
            ],
          }));
        }
      }

      setProgress({
        step: 'Szinkronizáció sikeresen befejeződött!',
        percent: 100,
        details: [
          `🎉 ${tenantName} sikeresen átkerült a felhőbe!`,
          `Összesen ${pkg.records?.length || 0} rekord és ${missingFiles.length} fájl szinkronizálva.`,
        ],
      });

      setSyncResult({
        message: `Sikeres PUSH! A(z) "${tenantName}" átkerült a felhőbe (${pkg.records?.length || 0} rekord, ${missingFiles.length} fájl szinkronizálva).`,
        type: 'ok',
        stats: applyRes.applied,
      });
      loadStatusAndTenants();
    } catch (err: any) {
      setSyncResult({ message: 'Hiba a feltöltés során: ' + (err.message || err), type: 'err' });
    } finally {
      setIsLoading(false);
    }
  };

  // ── PULL: Felhő → VPS / Localhost ────────────────────────────────
  const handlePull = async () => {
    setIsLoading(true);
    setSyncResult(null);
    setProgress({ step: 'Kapcsolódás a távoli felhőhöz...', percent: 10, details: [] });

    try {
      // 1. Távoli felhő hitelesítés
      let token = remoteToken;
      if (!token) {
        setProgress(prev => ({
          step: 'Távoli felhő hitelesítés...',
          percent: 20,
          details: [...(prev?.details || []), `Bejelentkezés: ${remoteEmail} @ ${remoteUrl}`],
        }));

        if (!remoteUrl || !remoteEmail || !remotePassword) {
          // S12: nincs beégetett fiók – kérjük a kapcsolati adatokat.
          setShowSettings(true);
          throw new Error('Add meg a távoli szerver címét, valamint a superadmin e-mail-címét és jelszavát a Kapcsolat beállításai panelen.');
        }
        const authRes = await ApiClient.loginRemote(remoteUrl, remoteEmail, remotePassword);
        if (!authRes.ok || !authRes.token) {
          throw new Error(`Távoli felhő bejelentkezés sikertelen: ${authRes.error}`);
        }
        token = authRes.token;
        setRemoteToken(token);
        localStorage.setItem('kf_remote_sync_token', token);
      }

      // 2. Kijelölt szervezet azonosítása (Helyi vagy Távoli Felhőbeli)
      let tenantIdToPull: number | undefined;
      let tenantSlugToPull: string | undefined;
      let tenantName = '';

      if (selectedTenantKey.startsWith('local_')) {
        tenantIdToPull = Number(selectedTenantKey.replace('local_', ''));
        const t = tenants.find(x => x.id === tenantIdToPull);
        tenantSlugToPull = t?.slug;
        tenantName = t ? t.name : `Szervezet #${tenantIdToPull}`;
      } else if (selectedTenantKey.startsWith('remote_')) {
        tenantSlugToPull = selectedTenantKey.replace('remote_', '');
        const rt = remoteTenants.find(x => x.slug === tenantSlugToPull);
        tenantName = rt ? rt.name : tenantSlugToPull;
      } else if (selectedTenantKey === 'custom') {
        tenantSlugToPull = customSlug.trim();
        tenantName = `/${tenantSlugToPull}`;
      } else {
        const featuredLocal = featuredSlug ? tenants.find(t => t.slug === featuredSlug) : undefined;
        if (featuredLocal) {
          tenantIdToPull = featuredLocal.id;
          tenantSlugToPull = featuredLocal.slug;
          tenantName = featuredLocal.name;
        } else if (tenants.length > 0) {
          tenantIdToPull = tenants[0].id;
          tenantSlugToPull = tenants[0].slug;
          tenantName = tenants[0].name;
        } else if (featuredSlug) {
          tenantSlugToPull = featuredSlug;
          tenantName = featuredName;
        }
      }

      setProgress({
        step: `Adatcsomag lehívása a felhőből (${tenantName})...`,
        percent: 35,
        details: [`Adatok lekérése a távoli felhőből: ${remoteUrl} (${tenantName})`],
      });

      const lastSyncVer = isDeltaSync ? (syncStatus?.latestEventId || 0) : 0;
      const pullPackageRes = await ApiClient.remoteRequest(remoteUrl, '/sync/export-package', token, {
        method: 'POST',
        body: JSON.stringify({
          tenant_id: tenantIdToPull,
          tenant_slug: tenantSlugToPull,
          since_version: lastSyncVer,
        }),
      });

      if (!pullPackageRes.ok || !pullPackageRes.package) {
        throw new Error(`Nem sikerült lehívni az adatokat a felhőből: ${pullPackageRes.error || 'A szervezet nem található a távoli felhőben'}`);
      }

      const pkg = pullPackageRes.package;
      setProgress(prev => ({
        step: 'Csomag összefésülése a helyi adatbázisban...',
        percent: 60,
        details: [
          ...(prev?.details || []),
          `Csomag megérkezett a felhőből: ${pkg.pages?.length || 0} oldal, ${pkg.records?.length || 0} rekord, ${pkg.attachments?.length || 0} melléklet`,
          'Helyi adatbázis rekordok és sémák frissítése...',
        ],
      }));

      // 3. Alkalmazás helyben
      const applyRes = await ApiClient.applySyncPackage(pkg);
      if (!applyRes.ok) {
        throw new Error(`Hiba az adatok helyi mentésekor: ${applyRes.error}`);
      }

      // 4. Hiányzó fájlok letöltése a távoli felhőből a helyi tárhelyre
      const missingFiles = applyRes.missing_files || [];
      if (missingFiles.length > 0) {
        setProgress(prev => ({
          step: `Hiányzó dokumentumok letöltése (0/${missingFiles.length})...`,
          percent: 78,
          details: [
            ...(prev?.details || []),
            `📦 ${missingFiles.length} db fájl letöltése a felhőből a helyi tárhelyre...`,
          ],
        }));

        let transferredCount = 0;
        const currentOrigin = window.location.origin;
        const localToken = ApiClient.getToken();

        for (let i = 0; i < missingFiles.length; i++) {
          const fileInfo = missingFiles[i];
          const transferRes = await ApiClient.transferSyncFile(
            remoteUrl,
            token,
            currentOrigin,
            localToken,
            fileInfo.r2_key,
            fileInfo.mime_type
          );

          if (transferRes.ok) {
            transferredCount++;
          }

          const pct = 78 + Math.round(((i + 1) / missingFiles.length) * 20);
          setProgress(prev => ({
            step: `Dokumentumok letöltése (${transferredCount}/${missingFiles.length})...`,
            percent: pct,
            details: [
              ...(prev?.details || []),
              `✓ ${fileInfo.file_name} letöltve (${i + 1}/${missingFiles.length})`,
            ],
          }));
        }
      }

      setProgress({
        step: 'Lehívás sikeresen befejeződött!',
        percent: 100,
        details: [
          `🎉 ${tenantName} adatai sikeresen szinkronizálva lettek a felhőből!`,
          `Frissítve: ${pkg.records?.length || 0} rekord, letöltve: ${missingFiles.length} fájl.`,
        ],
      });

      setSyncResult({
        message: `Sikeres PULL! A(z) "${tenantName}" frissítve a felhőből (${pkg.records?.length || 0} rekord, ${missingFiles.length} fájl letöltve).`,
        type: 'ok',
        stats: applyRes.applied,
      });
      loadStatusAndTenants();
    } catch (err: any) {
      setSyncResult({ message: 'Hiba a lehívás során: ' + (err.message || err), type: 'err' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-sm animate-in fade-in overflow-y-auto">
      <div className="w-full max-w-3xl rounded-2xl glass-panel border border-slate-700 p-5 sm:p-6 space-y-5 my-auto max-h-[90vh] overflow-y-auto">
        {/* Fejléc */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 text-cyan-400 flex items-center justify-center">
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </div>
            <div>
              <h3 className="font-bold text-base text-white flex items-center gap-2">
                <span>Tri-Target Szinkronizációs Központ</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                  v4.5 Élő Hálózat
                </span>
              </h3>
              <p className="text-xs text-slate-400">Cloudflare D1/R2 ⇄ VPS Docker ⇄ Localhost kétirányú szelektív replikáció</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white rounded-lg transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Eredmény üzenet */}
        {syncResult && (
          <div className={`p-3 rounded-xl border text-xs flex items-center gap-2.5 ${
            syncResult.type === 'ok'
              ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300'
              : 'bg-rose-500/15 border-rose-500/30 text-rose-300'
          }`}>
            {syncResult.type === 'ok' ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
            <div className="flex-1">
              <div className="font-semibold">{syncResult.message}</div>
              {syncResult.stats && (
                <div className="text-[11px] opacity-80 mt-0.5">
                  Oldalak: {syncResult.stats.pages} | Rekordok: {syncResult.stats.records} | Mellékletek: {syncResult.stats.attachments}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 1. SZELEKTÍV SZINKRONIZÁCIÓ – SZERVEZET VÁLASZTÓ */}
        <div className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-cyan-400" />
              <span className="font-bold text-xs text-white">Szelektív Szinkronizálás – Szervezet Kiválasztása</span>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={isDeltaSync}
                onChange={(e) => setIsDeltaSync(e.target.checked)}
                className="rounded border-slate-700 bg-slate-800 text-cyan-500 focus:ring-cyan-500"
              />
              <span>Csak a változások szinkronizálása (Delta szinkron)</span>
            </label>
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-slate-400 mb-1">
                  Kiválasztott szervezet:
                </label>
                <select
                  value={selectedTenantKey}
                  onChange={(e) => setSelectedTenantKey(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 font-medium"
                >
                  <optgroup label="🏠 Helyi adatbázisban létező szervezetek">
                    {tenants.map(t => (
                      <option key={`local_${t.id}`} value={`local_${t.id}`}>
                        {t.name} (/{t.slug})
                      </option>
                    ))}
                  </optgroup>

                  {/* Távoli felhőben elérhető, helyileg még nem létező szervezetek */}
                  {remoteTenants.filter(rt => !tenants.some(lt => lt.slug === rt.slug)).length > 0 && (
                    <optgroup label="☁️ Távoli Felhőben elérhető (Lehívható új szervezetek)">
                      {remoteTenants
                        .filter(rt => !tenants.some(lt => lt.slug === rt.slug))
                        .map(rt => (
                          <option key={`remote_${rt.slug}`} value={`remote_${rt.slug}`}>
                            ✨ {rt.name} (/{rt.slug}) [Felhőből lehívható]
                          </option>
                        ))}
                    </optgroup>
                  )}

                  {/* Konfigurált kiemelt szervezet (VITE_FEATURED_SYNC_SLUG), ha még nincs a listákban */}
                  {featuredSlug && !tenants.some(t => t.slug === featuredSlug) && !remoteTenants.some(rt => rt.slug === featuredSlug) && (
                    <optgroup label="☁️ Felhőből lehívható szervezet">
                      <option value={`remote_${featuredSlug}`}>
                        ✨ {featuredName} (/{featuredSlug}) [Felhőből lehívható]
                      </option>
                    </optgroup>
                  )}

                  <optgroup label="Egyéni opciók">
                    <option value="custom">✏️ Egyéni szervezet slug megadása (pl. minta-kozseg)...</option>
                  </optgroup>
                </select>
              </div>

              <div className="text-xs text-slate-400 bg-slate-950/60 p-2.5 rounded-xl border border-slate-800/80 flex flex-col justify-center">
                <div className="text-[11px] text-slate-500">Kiválasztott szervezet státusza:</div>
                <div className="text-slate-200 font-semibold mt-0.5 flex items-center gap-1.5 truncate">
                  {selectedTenantKey.startsWith('remote_') || (!tenants.some(t => selectedTenantKey === `local_${t.id}`) && selectedTenantKey !== 'custom') ? (
                    <>
                      <span className="w-2 h-2 rounded-full bg-cyan-400"></span>
                      <span className="text-cyan-300">Felhőben létezik (Kattints a "Változások Lehívása" gombra)</span>
                    </>
                  ) : (
                    <>
                      <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                      <span>
                        {tenants.find(t => `local_${t.id}` === selectedTenantKey)?.name || customSlug || 'Kiválasztva'}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {selectedTenantKey === 'custom' && (
              <div className="pt-2 border-t border-slate-800/60 flex items-center gap-2">
                <span className="text-[11px] text-slate-400 shrink-0">Kézzel megadott slug:</span>
                <input
                  type="text"
                  value={customSlug}
                  onChange={(e) => setCustomSlug(e.target.value)}
                  placeholder="pl. minta-kozseg"
                  className="flex-1 px-3 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-500 font-mono"
                />
              </div>
            )}
          </div>
        </div>

        {/* 2. TÁVOLI FELHŐ KAPCSOLAT ÉS BEÁLLÍTÁSOK */}
        <div className="p-4 rounded-xl bg-slate-900/60 border border-slate-800 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-indigo-400" />
              <span className="font-bold text-xs text-white">Központi Felhő Célpont (Hub)</span>
              {remoteStatus === 'connected' ? (
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  Kapcsolódva
                </span>
              ) : remoteStatus === 'checking' ? (
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1">
                  <RefreshCw className="w-2.5 h-2.5 animate-spin" />
                  Ellenőrzés...
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-rose-500/20 text-rose-300 border border-rose-500/30">
                  Nincs kapcsolat
                </span>
              )}
            </div>

            <button
              onClick={() => setShowSettings(!showSettings)}
              className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition"
            >
              <Sliders className="w-3.5 h-3.5" />
              <span>{showSettings ? 'Beállítások elrejtése' : 'Kapcsolat beállítások'}</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="text"
              value={remoteUrl}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder="https://api.example.hu"
              className="flex-1 px-3 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-slate-200 font-mono focus:outline-none focus:ring-1 focus:ring-cyan-500"
            />
            <button
              onClick={() => checkRemoteConnection()}
              disabled={isLoading}
              className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-semibold transition border border-slate-700"
            >
              Teszt
            </button>
          </div>

          {showSettings && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-slate-800/80 animate-in fade-in">
              <div>
                <label className="block text-[10px] text-slate-400">Távoli admin e-mail:</label>
                <input
                  type="email"
                  value={remoteEmail}
                  onChange={(e) => setRemoteEmail(e.target.value)}
                  className="w-full px-2.5 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-slate-200"
                />
              </div>
              <div>
                <label className="block text-[10px] text-slate-400">Távoli admin jelszó:</label>
                <input
                  type="password"
                  value={remotePassword}
                  onChange={(e) => setRemotePassword(e.target.value)}
                  className="w-full px-2.5 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-xs text-slate-200"
                />
              </div>
            </div>
          )}
        </div>

        {/* FOLYAMATJELZŐ (PROGRESS & LOG) */}
        {progress && (
          <div className="p-4 rounded-xl bg-slate-950 border border-cyan-500/30 space-y-2.5">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-cyan-300 flex items-center gap-1.5">
                <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                {progress.step}
              </span>
              <span className="font-mono text-cyan-400 font-bold">{progress.percent}%</span>
            </div>

            <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-500 to-emerald-400 transition-all duration-300"
                style={{ width: `${progress.percent}%` }}
              ></div>
            </div>

            <div className="max-h-28 overflow-y-auto font-mono text-[11px] text-slate-400 space-y-1 bg-slate-900/60 p-2.5 rounded-lg border border-slate-800">
              {progress.details.map((d, i) => (
                <div key={i} className="leading-relaxed">{d}</div>
              ))}
            </div>
          </div>
        )}

        {/* 3 CSOMÓPONT VIZUALIZÁCIÓ */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* 1. Cloudflare Hub (Master) */}
          <div className="p-3.5 rounded-xl bg-blue-950/30 border border-blue-500/30 text-center space-y-1.5 relative">
            <div className="w-7 h-7 mx-auto rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center">
              <Cloud className="w-4 h-4" />
            </div>
            <div className="font-bold text-xs text-white">Cloudflare (Hub)</div>
            <div className="text-[10px] text-blue-300 font-mono">D1 SQL + R2 Blob</div>
            <span className="inline-block px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-[9px] font-semibold">
              Központi Igazságforrás
            </span>
          </div>

          {/* 2. Külső MySQL & Tárhely Tükör */}
          <div className="p-3.5 rounded-xl bg-emerald-950/30 border border-emerald-500/30 text-center space-y-1.5">
            <div className="w-7 h-7 mx-auto rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
              <Database className="w-4 h-4" />
            </div>
            <div className="font-bold text-xs text-white">{DEPLOYMENT.mirrorLabel} (MySQL)</div>
            <div className="text-[10px] text-emerald-300 font-mono">30 GB Tárhely Tükör</div>
            <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[9px] font-semibold">
              Katasztrófatűrő másolat
            </span>
          </div>

          {/* 3. Helyi Docker / VPS */}
          <div className="p-3.5 rounded-xl bg-slate-900 border border-slate-700 text-center space-y-1.5">
            <div className="w-7 h-7 mx-auto rounded-lg bg-slate-800 text-slate-300 flex items-center justify-center">
              <HardDrive className="w-4 h-4" />
            </div>
            <div className="font-bold text-xs text-white">VPS / Localhost</div>
            <div className="text-[10px] text-slate-400 font-mono">workerd + SQLite Volume</div>
            <span className="inline-block px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 text-[9px] font-semibold">
              Aktuális példány
            </span>
          </div>
        </div>

        {/* MŰVELETI GOMBOK */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-800 flex-wrap gap-2">
          <button
            onClick={loadStatusAndTenants}
            disabled={isLoading}
            className="p-2 text-slate-400 hover:text-white rounded-lg transition text-xs flex items-center gap-1.5 cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Állapot frissítése</span>
          </button>

          <div className="flex items-center gap-2.5">
            <button
              onClick={handlePull}
              disabled={isLoading}
              className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-emerald-300 hover:text-emerald-200 font-semibold text-xs border border-emerald-500/30 flex items-center gap-1.5 transition disabled:opacity-50 cursor-pointer shadow-sm"
              title="A kiválasztott szervezet adatainak és dokumentumainak lehívása a felhőből"
            >
              <ArrowDownRight className="w-4 h-4 text-emerald-400" />
              <span>Változások Lehívása (Pull ← Felhő)</span>
            </button>

            <button
              onClick={handlePush}
              disabled={isLoading}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold text-xs shadow-lg shadow-cyan-600/20 flex items-center gap-1.5 transition disabled:opacity-50 cursor-pointer"
              title="A kiválasztott szervezet (pl. Citadella Kft.) adatainak és 133 dokumentumának feltöltése a felhőbe"
            >
              <ArrowUpRight className="w-4 h-4" />
              <span>Helyi Adatok Beküldése (Push → Felhő)</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
